import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getOrganizationAssistantsAPI,
  getOrganizationUsersAPI,
  getOrganization,
  getOnboardingAssistantsAPI,
} from "./organizationAPI";

const ONBOARDING_GROUPS = {
  TASKS: [],
  EMAIL_CALENDAR: [],
  COMMUNICATION: [],
};

const initialState = {
  organization: null,
  organizations: [],
  onboardingGroups: ONBOARDING_GROUPS,
  users: [],
  loading: false,
  error: null,
};

export const getOrganizationAssistants = createAsyncThunk(
  "organization/getOrganizationAssistants",
  async ({ search = "" }, thunkAPI) => {
    try {
      const res = await getOrganizationAssistantsAPI(search);

      return res.data.organizations || [];
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message,
      );
    }
  },
);

export const getOnboardingAssistants = createAsyncThunk(
  "organization/getOnboardingAssistants",
  async (_, thunkAPI) => {
    try {
      const res = await getOnboardingAssistantsAPI();

      return res.data.onboardingGroups || ONBOARDING_GROUPS;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message,
      );
    }
  },
);

export const getOrganizationUsers = createAsyncThunk(
  "organization/getOrganizationUsers",
  async (_, thunkAPI) => {
    try {
      const res = await getOrganizationUsersAPI();
      return res.data.users || [];
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message,
      );
    }
  },
);

export const getOrganizationByUuid = createAsyncThunk(
  "organization/getOrganization",
  async (uuid, thunkAPI) => {
    try {
      const res = await getOrganization(uuid);
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message,
      );
    }
  },
);

const organizationSlice = createSlice({
  name: "organization",
  initialState,
  reducers: {
    clearOrganizationData: (state) => {
      state.organizations = [];
      state.onboardingGroups = ONBOARDING_GROUPS;
      state.users = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOrganizationAssistants.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getOrganizationAssistants.fulfilled, (state, action) => {
        state.loading = false;
        state.organizations = action.payload;
      })
      .addCase(getOrganizationAssistants.rejected, (state, action) => {
        state.loading = false;
        state.organizations = [];
        state.error = action.payload;
      })

      .addCase(getOnboardingAssistants.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getOnboardingAssistants.fulfilled, (state, action) => {
        state.loading = false;
        state.onboardingGroups = action.payload;
      })
      .addCase(getOnboardingAssistants.rejected, (state, action) => {
        state.loading = false;
        state.onboardingGroups = ONBOARDING_GROUPS;
        state.error = action.payload;
      })

      .addCase(getOrganizationUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getOrganizationUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload;
      })
      .addCase(getOrganizationUsers.rejected, (state, action) => {
        state.loading = false;
        state.users = [];
        state.error = action.payload;
      })

      .addCase(getOrganizationByUuid.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getOrganizationByUuid.fulfilled, (state, action) => {
        state.loading = false;
        state.organization = action.payload.data;
      })
      .addCase(getOrganizationByUuid.rejected, (state, action) => {
        state.loading = false;
        state.organization = null;
        state.error = action.payload;
      });
  },
});

export const { clearOrganizationData } = organizationSlice.actions;

export default organizationSlice.reducer;
